import {observer} from "mobx-react";
import React, {useEffect, useRef, useState} from 'react';
import {
    CircularProgress,
    Input
} from '@mui/material';
import {useStores} from "../../../Hooks";
import SearchResults from "./SearchResults";
import { useLocation } from 'react-router-dom';
import CheckIcon from "@mui/icons-material/Check";
import {procedureTypeEnum} from "../../../serverClientShared/sharedEnums";
import CloseIcon from '@mui/icons-material/Close';

const CategoryEnum = {
    ALL: 'All',
    PROJECTS: 'Projects',
    DIGITAL_TWIN_MODELS: 'Digital twin models',
    INTERACTIVE_FLOWS: 'Interactive flows',
    MEDIA_ITEMS: 'Media items',
};

const SearchDialog = observer(({handleClose, acceptableRoles}) => {
    const location = useLocation();
    const {generalStore, projectStore} = useStores();
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [filteredResults, setFilteredResults] = useState([]);
    const isInProjectDirectory = location.pathname.startsWith('/project/') || location.pathname.startsWith('/edit/');
    const [clicked, setClicked] = useState(true);
    const [queue, setQueue] = useState('');
    const [isHovered, setIsHovered] = useState(false);

    const inputRef = useRef(null);

    const handleCategoryClick = (category) => {
        setSelectedCategory(category);
    };
    const asyncSearch = async (query) => {
        setIsSearching(true);

        const timeoutPromise = new Promise((resolve, reject) => {
            const id = setTimeout(() => {
                clearTimeout(id);
                reject(new Error('Search timed out after 3 seconds'));
            }, 3000);
        });

        try {
            const uniqAns = await Promise.race([
                generalStore.globalSearch(query),
                timeoutPromise
            ]);

            const filteredResults = setProjectOnly(uniqAns, true)
            setResults(filteredResults);

            if (selectedCategory === CategoryEnum.ALL) {
                setFilteredResults(filteredResults);
            } else if (selectedCategory === CategoryEnum.PROJECTS) {
                setFilteredResults(filteredResults.filter(result => result.type === 'projects'));
            } else if (selectedCategory === CategoryEnum.DIGITAL_TWIN_MODELS) {
                setFilteredResults(filteredResults.filter(result => result.type === 'procedures' && result?.ProcedureType === procedureTypeEnum.FUSION));
            } else if (selectedCategory === CategoryEnum.INTERACTIVE_FLOWS) {
                setFilteredResults(filteredResults.filter(result => result.type === 'procedures' && result?.ProcedureType === procedureTypeEnum.REGULAR));
            } else if (selectedCategory === CategoryEnum.MEDIA_ITEMS) {
                setFilteredResults(filteredResults.filter(result => result.type === 'procedures' && result?.ProcedureType === procedureTypeEnum.MEDIA));
            }
        } catch (error) {
            console.error('Search failed:', error);
            setResults([]);
            setFilteredResults([]);
        } finally {
            setIsSearching(false);
        }
    }

    const setFilter = (results) => {
        const filteredResults = setProjectOnly(results, true)
        if (selectedCategory === CategoryEnum.ALL) {
            setFilteredResults(filteredResults);
        } else if (selectedCategory === CategoryEnum.PROJECTS) {
            setFilteredResults(filteredResults.filter(result => result.type === 'projects'));
        } else if (selectedCategory === CategoryEnum.DIGITAL_TWIN_MODELS) {
            setFilteredResults(filteredResults.filter(result => result.type === 'procedures' && result?.ProcedureType === procedureTypeEnum.FUSION));
        } else if (selectedCategory === CategoryEnum.INTERACTIVE_FLOWS) {
            setFilteredResults(filteredResults.filter(result => result.type === 'procedures' && result?.ProcedureType === procedureTypeEnum.REGULAR));
        } else if (selectedCategory === CategoryEnum.MEDIA_ITEMS) {
            setFilteredResults(filteredResults.filter(result => result.type === 'procedures' && result?.ProcedureType === procedureTypeEnum.MEDIA));
        }
    }

    const setProjectOnly = (results, shouldReturn = false) => {
        if (!isInProjectDirectory || !clicked) {
            return results;
        }
        const projectId = projectStore.currentProject?.ProjectIdentifier
        const thisProject = results.filter(result => {
            const projectRef = result.type === 'projects' ? result.ProjectIdentifier : result.ProjectRef;
            return projectId === projectRef
        })
        if (shouldReturn) {
            return thisProject;
        }
        setResults(thisProject);
        setFilter(thisProject);
    }

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        if (query.length > 0) {
            if (!isSearching) {
                asyncSearch(query);
                setQueue('');
            } else {
                setQueue(query);
            }
        } else {
            setFilteredResults([])
            setResults([])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, clicked]);

    useEffect(() => {
        setFilter(results);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory]);

    useEffect(() => {
        if (!isSearching && queue.length > 0) {
            if (queue === query) {
                asyncSearch(queue);
            }
            setQueue('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSearching]);

    return (<>
        <div className="search-global-input-container">
            <Input
                className="search-global-input-searchBox"
                type="text"
                value={query}
                maxLength={20}
                onChange={e => setQuery(e.target.value)}
                placeholder={generalStore.translate('Search for Projects, Digital twins, Interactive flows and media items...')}
                inputRef={inputRef}
            />
            {query.length >= 1 && (
                <button
                    className={isHovered ? "clear-input-hvd" : "clear-input"}
                    onClick={() => {
                        setQuery('')
                        setIsHovered(false)
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    aria-label="Clear input" // Improved accessibility
                >
                    <CloseIcon fontSize="small"/>
                </button>
            )}
            <div className="search-global-input-categoryBox">
                {Object.values(CategoryEnum).map((category) => (
                    <div
                        key={category}
                        className={`search-global-input-category ${selectedCategory !== category ? 'search-global-input-categoryInactive' : ''}`}
                        onClick={() => handleCategoryClick(category)}
                    >
                        {generalStore.translate(category)}
                    </div>
                ))}
                {isInProjectDirectory && (
                    <div
                        key={'123'}
                        className={`search-global-input-categoryTick ${!clicked ? 'search-global-input-categoryInactive' : ''}`}
                        onClick={() => setClicked(!clicked)}
                    >
                        {'This project'}{clicked && (<CheckIcon style={{fontSize: '1rem'}}/>)}
                    </div>
                )}
            </div>
            <div className="search-global-input-divider"></div>

            {isSearching ? (
                <div className="search-global-input-centerContent">
                    <CircularProgress className="search-global-input-progress"/>
                </div>
            ) : (
                <div>
                    {(results.length > 0) && (query.length > 0) ? (
                        <SearchResults
                            results={filteredResults}
                            searchInput={query}
                            handleClose={handleClose}
                            editPromissions={acceptableRoles.IsWorkspaceOwner || acceptableRoles.isCC}
                        />
                    ) : (
                        <div className='search-global-input-searchHistory'>
                            <div className='search-global-input-historyText'>{query.length === 0
                                ? generalStore.translate('No results. Start typing')
                                : generalStore.translate('No results found')}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    </>)
});

export default SearchDialog;
